import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(
    () => sessionStorage.getItem('admin-password-recovery') === 'true',
  );

  useEffect(() => {
    checkAuth();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        sessionStorage.setItem('admin-password-recovery', 'true');
        setIsPasswordRecovery(true);
        setUser(session?.user ?? null);
        setIsLoading(false);
      } else if (event === 'SIGNED_IN') {
        checkAuth();
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // Check if the user is an admin
        const { data: adminCheck, error: adminError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (!adminError && adminCheck) {
          setIsAuthenticated(true);
          setUser(session.user);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      
      // Check if the logged in user is an admin
      const { data: adminCheck, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', data.user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (adminError || !adminCheck) {
        return {
          success: false,
          code: 'not_admin',
          error: 'Access denied. This account is not authorized for admin access.',
        };
      }
      
      setIsAuthenticated(true);
      setUser(data.user);
      return { success: true };
    } catch (error: any) {
      console.error('Login failed:', error.message);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setIsAuthenticated(false);
      setUser(null);
      return { success: true };
    } catch (error: any) {
      console.error('Logout failed:', error.message);
      return { success: false, error: error.message };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${window.location.pathname}`,
      });
      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Password reset failed:', error.message);
      return { success: false, error: error.message };
    }
  };

  const bootstrapFirstAdmin = async (bootstrapToken: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('bootstrap-first-admin', {
        body: { bootstrap_token: bootstrapToken },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await checkAuth();
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message ?? 'Admin bootstrap failed' };
    }
  };

  const updateRecoveredPassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setIsPasswordRecovery(false);
      sessionStorage.removeItem('admin-password-recovery');
      await supabase.auth.signOut();
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message ?? 'Password update failed' };
    }
  };

  return {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    resetPassword,
    bootstrapFirstAdmin,
    isPasswordRecovery,
    updateRecoveredPassword,
  };
}
